﻿import { useMsal } from '@azure/msal-react';
import { Button, mergeClasses, SearchBox, Text } from '@fluentui/react-components';
import { mapAsync } from 'es-toolkit/array';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useCommonStyles, useStores } from '../../common/hooks';
import { AwardGroup, AwardGroupRef, AwardRequest, GroupRequest } from '../../models';
import { ConfirmationDialog, CustomMessageBar, PageTitle } from '../shared-components';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import { Prompt } from '../shared-components/Prompt';
import { AddOrEditAwardModal } from './components/AddOrEditAwardModal';
import { AddOrEditGroupModal } from './components/AddOrEditGroupModal';
import { AwardDetailView } from './components/AwardDetailView';
import { AwardGroupList } from './components/AwardGroupList';
import { MoveAwardRequest } from '../../models/move-award-request';
import './ManageAwardListPage.scss';

const ManageAwardListPageImpl: React.FC = () => {
  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    awardStore,
    manageAwardListPageUiStore,
  } = useStores();

  const commonStyles = useCommonStyles();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>('');
  const [isShowSaveSuccessToast, setShowSaveSuccessToast] = useState<boolean>(false);

  const getAwardById = async (awardId: number): Promise<void> => {
    try {
      const award = await apiStore.getAwardById(instance, awardId);
      manageAwardListPageUiStore.setSelectedAward(award);
    } catch (error) {
      // TODO: handle error
    }
  };

  const createAward = async (award: AwardRequest): Promise<void> => {
    try {
      await apiStore.createAward(instance, award);
    } catch (error) {
      // TODO: handle error
    }
  };

  const moveAward = async (awardId: number, moveAwardRequest: MoveAwardRequest): Promise<void> => {
    try {
      await apiStore.moveAward(instance, awardId, moveAwardRequest);
    } catch (error) {
      // TODO: handle error
    }
  };

  const deleteAward = async (awardId: number): Promise<void> => {
    try {
      await apiStore.deleteAward(instance, awardId);
    } catch (error) {
      // TODO: handle error
    }
  };

  const createGroup = async (temporaryGroupId: number, groupRequest: GroupRequest): Promise<void> => {
    try {
      const groupId = await apiStore.createGroup(instance, groupRequest);
      manageAwardListPageUiStore.replaceTemporaryGroupId(temporaryGroupId, groupId);
    } catch (error) {
      // TODO: handle error
    }
  };

  const updateGroup = async (groupId: number, groupRequest: GroupRequest): Promise<void> => {
    try {
      await apiStore.updateGroup(instance, groupId, groupRequest);
    } catch (error) {
      // TODO: handle error
    }
  };

  const deleteGroup = async (groupId: number): Promise<void> => {
    try {
      await apiStore.deleteGroup(instance, groupId);
    } catch (error) {
      // TODO: handle error
    }
  };

  const onAwardSelected = async (awardGroupRef: AwardGroupRef): Promise<void> => {
    if (awardGroupRef.awardId > 0) {
      setIsLoading(true);
      await getAwardById(awardGroupRef.awardId);
      setIsLoading(false);
    } else {
      manageAwardListPageUiStore.createSelectedAwardFromRef(awardGroupRef);
    }
  };

  const onCreateGroup = (): void => {
    if (!manageAwardListPageUiStore.currentProgramId) return;

    manageAwardListPageUiStore.createGroupRequestBody();
    manageAwardListPageUiStore.setAddOrEditGroupModalType('add');
  };

  const onEditGroup = (awardGroup: AwardGroup): void => {
    if (!manageAwardListPageUiStore.currentProgramId) return;

    manageAwardListPageUiStore.createGroupRequestBodyForUpdate(awardGroup);
    manageAwardListPageUiStore.setAddOrEditGroupModalType('update');
  };

  const onDismissAddOrEditGroupModal = (): void => {
    manageAwardListPageUiStore.setAddOrEditGroupModalType(undefined);
  };

  const onAddOrEditGroup = (): void => {
    if (!manageAwardListPageUiStore.groupRequestBody) return;

    if (manageAwardListPageUiStore.addOrEditGroupModalType === 'add') {
      manageAwardListPageUiStore.addNewGroup();
      return;
    }

    manageAwardListPageUiStore.updateGroup();
  };

  const onCreateAward = (): void => {
    if (!manageAwardListPageUiStore.currentProgramId) return;

    manageAwardListPageUiStore.createAwardRequestBody();
    manageAwardListPageUiStore.setAddOrEditAwardModalType('add');
  };

  const onDismissAddOrEditAwardModal = (): void => {
    manageAwardListPageUiStore.setAddOrEditAwardModalType(undefined);
  };

  const onAddOrEditAward = (): void => {
    if (!manageAwardListPageUiStore.awardRequestBody) return;

    if (manageAwardListPageUiStore.addOrEditAwardModalType === 'add') {
      manageAwardListPageUiStore.addNewAward();
      return;
    }

    manageAwardListPageUiStore.updateAward();
  };

  const onRestoreAward = (award: AwardGroupRef): void => {
    manageAwardListPageUiStore.restoreAward(award);
  };

  const onEditAward = (award: AwardGroupRef): void => {
    manageAwardListPageUiStore.createAwardRequestBodyForUpdate(award);
    manageAwardListPageUiStore.setAddOrEditAwardModalType('update');
  };

  const handleCreateGroups = async (programId: number): Promise<void> => {
    await mapAsync(
      manageAwardListPageUiStore.addedGroups,
      async (group) => {
        await createGroup(group.awardGroupId, {
          programId,
          displayName: group.title,
        });
      },
      { concurrency: 1 },
    );
  };

  const handleUpdateGroups = async (programId: number): Promise<void> => {
    await mapAsync(
      [...manageAwardListPageUiStore.updatedGroupMap.values()],
      async (group) => updateGroup(group.awardGroupId, {
        programId,
        displayName: group.title,
      }),
    );
  };

  const handleDeleteGroups = async (): Promise<void> => {
    await mapAsync(
      [...manageAwardListPageUiStore.deletedGroupIds],
      async (groupId) => deleteGroup(groupId),
    );
  };

  const handleCreateAwards = async (): Promise<void> => {
    await mapAsync(
      manageAwardListPageUiStore.addedAwards,
      async (award) => createAward(award),
    );
  };

  const handleMoveAwards = async (): Promise<void> => {
    await mapAsync(
      [...manageAwardListPageUiStore.updatedAwardMap.values()],
      async (award) => moveAward(award.awardId, award),
    );
  };

  const handleDeleteAwards = async (): Promise<void> => {
    await mapAsync(
      [...manageAwardListPageUiStore.deletedAwardIds.values()],
      async (awardId) => deleteAward(awardId),
    );
  };

  const onSaveButtonClicked = async (): Promise<void> => {
    setIsLoading(true);

    if (!manageAwardListPageUiStore.currentProgramId) {
      setIsLoading(false);
      return;
    }

    await handleCreateGroups(manageAwardListPageUiStore.currentProgramId);
    await handleDeleteAwards();

    await Promise.allSettled([
      handleCreateAwards(),
      handleMoveAwards(),
      handleUpdateGroups(manageAwardListPageUiStore.currentProgramId),
      handleDeleteGroups(),
    ]);

    await preloadData();

    setShowSaveSuccessToast(true);
    setTimeout(() => setShowSaveSuccessToast(false), 4000);

    setIsLoading(false);
  };

  const debouncedSearch = useDebouncedCallback(
    async (query: string) => {
      setIsLoading(true);
      try {
        const awardGroups = await apiStore.getAwardGroups(instance, { searchText: query });
        manageAwardListPageUiStore.setAwardGroups(awardGroups);
      } catch (error) {
        // TODO: handle error
      } finally {
        setIsLoading(false);
      }
    },
    1000
  );

  const onSearchTextChanged = (value: string): void => {
    setSearchText(value);
    debouncedSearch(value);
  }

  const getDefaultProgramRef = async (): Promise<void> => {
    try {
      const defaultProgramRef = await apiStore.getDefaultProgramRef(instance);
      manageAwardListPageUiStore.setCurrentProgramId(defaultProgramRef.programId);
    } catch (error) {
      // TODO: handle error
    }
  }

  const getAdminAwardGroupRefs = async (): Promise<void> => {
    try {
      const awardGroups = await apiStore.getAdminAwardGroups(instance, {
        includeCountryAward: true,
      });
      manageAwardListPageUiStore.setAwardGroups(awardGroups);
    } catch (error) {
      // TODO: handle error
    }
  };

  const getMasterAwards = async (): Promise<void> => {
    try {
      const masterAwards = await apiStore.getMasterAwards(instance);
      manageAwardListPageUiStore.setMasterAwards(masterAwards);
    } catch (error) {
      // TODO: handle error
    }
  };

  const preloadData = async (): Promise<void> => {
    setIsLoading(true);

    if (!awardStore.defaultProgramRef) {
      await getDefaultProgramRef();
    } else {
      manageAwardListPageUiStore.setCurrentProgramId(awardStore.defaultProgramRef.programId);
    }

    await Promise.allSettled([
      getAdminAwardGroupRefs(),
      getMasterAwards(),
    ]);

    setIsLoading(false);
  };

  useEffect(() => {
    preloadData();
    return () => {
      manageAwardListPageUiStore.resetStates();
    }
  }, []);

  useEffect(() => () => {
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  return (
    <FlexBox
      isFullWidth
      direction='column'
      className='manage-award-list-page'
    >
      <Prompt
        when={manageAwardListPageUiStore.hasChanges}
      />
      {
        isShowSaveSuccessToast &&
        <CustomMessageBar
          message='Awards successfully saved'
          notificationTypeCode='success'
          showDismissButton
        />
      }
      {
        manageAwardListPageUiStore.addOrEditGroupModalType !== undefined &&
        <AddOrEditGroupModal
          isModalOpen={manageAwardListPageUiStore.addOrEditGroupModalType !== undefined}
          onDismissModal={() => onDismissAddOrEditGroupModal()}
          onSaveButtonClicked={() => onAddOrEditGroup()}
        />
      }
      {
        manageAwardListPageUiStore.selectedAwardGroup && (
          <ConfirmationDialog
            isOpen
            dialogTitle='Confirm Delete Award Group'
            dialogSubtext={
              <Text as='p'>
                Are you sure you want to delete this "{manageAwardListPageUiStore.selectedAwardGroup.title}" award group?
              </Text>
            }
            onPrimaryButtonClick={() => manageAwardListPageUiStore.deleteAwardGroup()}
            onSecondaryButtonClick={() => manageAwardListPageUiStore.setSelectedAwardGroup(undefined)}
            primaryButtonLabel='Yes'
            secondaryButtonLabel='No, go back'
            onCloseDialogClick={() => manageAwardListPageUiStore.setSelectedAwardGroup(undefined)}
          />
        )
      }
      {
        manageAwardListPageUiStore.addOrEditAwardModalType !== undefined &&
        <AddOrEditAwardModal
          isModalOpen={manageAwardListPageUiStore.addOrEditAwardModalType !== undefined}
          onDismissModal={() => onDismissAddOrEditAwardModal()}
          onSaveButtonClicked={() => onAddOrEditAward()}
        />
      }
      {
        manageAwardListPageUiStore.selectedAwardGroupRef && (
          <ConfirmationDialog
            isOpen
            dialogTitle='Confirm Delete Award'
            dialogSubtext={
              <p className='fs-6'>
                Are you sure you want to delete this "{manageAwardListPageUiStore.selectedAwardGroupRef.title}" award?
              </p>
            }
            onPrimaryButtonClick={() => manageAwardListPageUiStore.deleteAward()}
            onSecondaryButtonClick={() => manageAwardListPageUiStore.setSelectedAwardGroupRef(undefined)}
            primaryButtonLabel='Yes'
            secondaryButtonLabel='No, go back'
            onCloseDialogClick={() => manageAwardListPageUiStore.setSelectedAwardGroupRef(undefined)}
          />
        )
      }
      {
        isLoading && <Loading />
      }
      {
        manageAwardListPageUiStore.currentProgramId && (
          <>
            <FlexBox
              content='space-between'
              aligns='end'
            >
              <PageTitle
                title='Manage Award List'
                subtitle={awardStore.defaultProgramRef?.displayName}
              />
              <Button
                appearance='primary'
                disabled={!manageAwardListPageUiStore.hasChanges}
                onClick={() => onSaveButtonClicked()}
              >
                Save all
              </Button>
            </FlexBox>
            <FlexBox
              isFullWidth
              className='manage-award-list-page__main'
            >
              <div className='main-sidebar'>
                <div className='main-sidebar__search-box'>
                  <SearchBox
                    id='award-group-search'
                    placeholder='Search'
                    value={searchText}
                    className={mergeClasses(commonStyles.inputField, 'search-box__input')}
                    onChange={(_, data) => onSearchTextChanged(data.value)}
                  />
                </div>
                <AwardGroupList
                  onAwardSelected={(award) => onAwardSelected(award)}
                  onCreateGroup={() => onCreateGroup()}
                  onEditGroup={onEditGroup}
                  onDeleteGroup={(awardGroup) => manageAwardListPageUiStore.setSelectedAwardGroup(awardGroup)}
                  onCreateAward={onCreateAward}
                  onRestoreAward={(award) => onRestoreAward(award)}
                  onEditAward={onEditAward}
                  onDeleteAward={(award) => manageAwardListPageUiStore.setSelectedAwardGroupRef(award)}
                />
              </div>
              {
                manageAwardListPageUiStore.selectedAward ? (
                  <AwardDetailView
                    selectedAward={manageAwardListPageUiStore.selectedAward}
                  />
                ) : (
                  <Text
                    as='p'
                    className='page-fallback'
                  >
                    Sorry, no award is currently selected.
                  </Text>
                )
              }
            </FlexBox>
          </>
        )
      }
      {
        !manageAwardListPageUiStore.currentProgramId && (
          <Text
            className='page-fallback'
            as='p'
          >
            Sorry, no program is currently selected.
          </Text>
        )
      }
    </FlexBox>
  );
}

export const ManageAwardListPage = observer(ManageAwardListPageImpl);
